import React, { useEffect } from "react";
import { 
  Trophy, Target, Search, Save, Zap, Eye, ThumbsUp, 
  CheckSquare, Flame, HelpCircle, ChevronUp, ChevronDown, 
  CheckCircle, Sparkles, ChevronRight, Lock
} from "lucide-react";
import { bizDebug } from "../utils/debugLogger";
import { getActiveBoosterDisplay } from "../utils/boosterMetadata";

interface Quest {
  id: string;
  quest_key: string;
  name: string;
  description: string;
  cadence: "daily" | "weekly" | "one_time" | "pro" | "all";
  target_count: number;
  current_progress: number;
  is_completed: boolean;
  is_claimed: boolean;
  points_reward: number;
  pro_booster_duration?: string;
  event_type: string;
  explanation?: string;
}

/**
 * State-based view calculation for quest categories.
 * Determines category state: 'locked' | 'completed' | 'in_progress' | 'available'
 */
export function getQuestCategoryViewState({
  category,
  isProUser,
  quests
}: {
  category: "all" | "daily" | "weekly" | "one_time" | "pro";
  isProUser: boolean;
  quests: Quest[];
}): "locked" | "completed" | "in_progress" | "available" {
  if (category === "pro" && !isProUser) {
    return "locked";
  }

  const categoryQuests = quests.filter((q) => {
    if (q.cadence === "pro" && !isProUser) return false;
    if (category === "all") return true;
    return q.cadence === category;
  });

  if (categoryQuests.length === 0) {
    return "available";
  }

  const allCompleted = categoryQuests.every((q) => q.is_completed || q.is_claimed);
  if (allCompleted) {
    return "completed";
  }

  const anyStarted = categoryQuests.some((q) => (q.current_progress || 0) > 0);
  if (anyStarted) {
    return "in_progress";
  }

  return "available";
}

const getQuestActionInstructions = (quest: Quest) => {
  const count = quest.target_count || 1;
  
  if (quest.event_type === "business_search") {
    return {
      badge: "🎯 HOW TO COMPLETE",
      shortInstruction: `Type a business category or city (e.g. 'Coffee Shops in Denver') into the top Search Bar and run ${count} search${count > 1 ? "es" : ""}.`,
      steps: [
        `Navigate to the Search bar or Map View at the top of the app.`,
        `Enter a business category and city or ZIP code (e.g., 'Plumbers in Chicago').`,
        `Click 'Search' to execute ${count > 1 ? `${count} searches` : "a search"} and earn progress!`
      ]
    };
  }

  if (quest.event_type === "business_save") {
    return {
      badge: "🎯 HOW TO COMPLETE",
      shortInstruction: `Find any business result card and click the 'Save Business' (bookmark 🔖) button to add ${count} lead${count > 1 ? "s" : ""} to your CRM.`,
      steps: [
        `Search for local businesses or browse your current results.`,
        `Click the 'Save Business' button on any business card.`,
        `Repeat for ${count} business${count > 1 ? "es" : ""} to complete this quest!`
      ]
    };
  }

  if (quest.event_type === "contact_lookup") {
    return {
      badge: "🎯 HOW TO COMPLETE",
      shortInstruction: `Click 'Search Contacts' on ${count} business card${count > 1 ? "s" : ""} to extract verified email and phone details.`,
      steps: [
        `Open any business card in search results or saved pipeline.`,
        `Click the 'Search Contacts' button.`,
        `Contact details will be extracted and progress will automatically increment!`
      ]
    };
  }

  if (quest.event_type === "seo_audit") {
    return {
      badge: "🎯 HOW TO COMPLETE",
      shortInstruction: `Find a business card with a website link and click 'Run Review Website' for ${count} site${count > 1 ? "s" : ""}.`,
      steps: [
        `Locate a business result card that includes an active website URL.`,
        `Click the 'Run Review Website' button.`,
        `The technical website inspection will run and grant your quest progress.`
      ]
    };
  }

  if (quest.event_type === "profile_update") {
    return {
      badge: "🎯 HOW TO COMPLETE",
      shortInstruction: `Go to Settings (gear icon ⚙️) in the menu and update your theme, map style, or language preference.`,
      steps: [
        `Click the Settings gear icon in the header or sidebar menu.`,
        `Change any setting (App Theme, Map Style, or Language).`,
        `Click Save Settings to complete this task!`
      ]
    };
  }

  if (quest.event_type === "login") {
    return {
      badge: "🎯 HOW TO COMPLETE",
      shortInstruction: `Log into BizNearYou once each calendar day. Your daily attendance is automatically registered upon login.`,
      steps: [
        `Sign into your BizNearYou account once per calendar day.`,
        `Daily attendance is automatically logged as complete upon login!`
      ]
    };
  }

  if (quest.event_type === "lead_export") {
    return {
      badge: "🎯 HOW TO COMPLETE",
      shortInstruction: `Go to your Saved Businesses pipeline list and click the 'Export CSV' button at top right.`,
      steps: [
        `Navigate to your Saved Businesses pipeline view.`,
        `Click the 'Export CSV' button to download your saved leads.`,
        `The export action automatically triggers and completes this quest!`
      ]
    };
  }

  return {
    badge: "🎯 HOW TO COMPLETE",
    shortInstruction: quest.description || "Perform the requested action in the application to complete this scouting task.",
    steps: [
      `Read the task objective: "${quest.description}".`,
      `Perform the corresponding action in your workspace to earn progress.`
    ]
  };
};

interface TodaysScoutingQuestSectionProps {
  isProUser: boolean;
  questsLoading: boolean;
  quests: Quest[];
  activeQuestTab: "all" | "daily" | "weekly" | "one_time" | "pro";
  setActiveQuestTab: (tab: "all" | "daily" | "weekly" | "one_time" | "pro") => void;
  onUpgradeClick: () => void;
  expandedQuestId: string | null;
  setExpandedQuestId: (id: string | null) => void;
  claimQuest: (questKey: string) => void;
  stats: any;
  setIsBadgesModalOpen: (open: boolean) => void;
  settingsAppTheme: string;
  boosterTimeRemaining: string | null;
  claimingQuestKey?: string | null;
}

export const TodaysScoutingQuestSection: React.FC<TodaysScoutingQuestSectionProps> = ({
  isProUser,
  questsLoading,
  quests,
  activeQuestTab,
  setActiveQuestTab,
  onUpgradeClick,
  expandedQuestId,
  setExpandedQuestId,
  claimQuest,
  stats,
  setIsBadgesModalOpen,
  settingsAppTheme,
  boosterTimeRemaining,
  claimingQuestKey
}) => {
  useEffect(() => {
    bizDebug("TODAYS_SCOUTING_QUEST_RENDER", {
      isRendering: true,
      questCount: quests.length,
      boosterActive: !!boosterTimeRemaining,
      boosterTimeRemaining,
      badgeCount: stats?.achievements?.filter((a: any) => a?.unlockedAt)?.length || 0,
      isProUser,
      screenWidth: typeof window !== "undefined" ? window.innerWidth : null
    });
  }, [quests.length, boosterTimeRemaining, stats?.achievements, isProUser]);

  const isDark = settingsAppTheme === "dark";
  const cardClass = isDark ? "bg-slate-900 border-slate-800 hover:border-slate-700" : "bg-white border-slate-200/80 shadow-xs hover:shadow-sm";

  return (
    <div 
      data-component="TodaysScoutingQuestSection"
      className={`p-4 sm:p-5 rounded-2xl border ${cardClass} relative overflow-visible transition-all text-left space-y-5 h-auto min-h-fit`}
    >
      <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-50/35 rounded-full blur-3xl -z-10 overflow-hidden"></div>
      
      {/* Quest Section Title & Header Row */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 pb-3 border-b border-slate-150">
        <div className="space-y-1 min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Trophy className="w-4 h-4 text-slate-500 shrink-0" />
            <h2 className="text-xs font-black tracking-[0.18em] uppercase text-slate-800">
              Today's search tasks
            </h2>
          </div>
          <p className="text-[11px] text-slate-500 font-semibold leading-relaxed">
            Building a consistent routine is the best way to find a role. Keep track of your search targets, save busy workplaces, and build a steady daily habit.
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 shrink-0">
          {/* Tabs for Cadence selection */}
          <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 shrink-0 flex-wrap gap-1">
            {(["all", "daily", "weekly", "one_time", "pro"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveQuestTab(tab)}
                className={`px-2.5 py-1 text-[10.5px] font-black uppercase tracking-wider rounded-lg transition-all cursor-pointer ${
                  activeQuestTab === tab
                    ? "bg-white text-indigo-700 shadow-4xs border border-slate-250"
                    : "text-slate-550 hover:text-slate-800"
                }`}
              >
                {tab === "one_time" ? "One-Time" : tab === "pro" ? "Pro Quests" : tab}
              </button>
            ))}
          </div>

          {/* Blue pill/button as specified by the user */}
          <button
            type="button"
            onClick={onUpgradeClick}
            className="shrink-0 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-[11px] font-bold text-blue-700 cursor-pointer hover:bg-blue-100 transition-all text-center"
          >
            {isProUser ? "Quest Pro unlocked" : "Unlock Quest Pro"}
          </button>
        </div>
      </div>

      {/* Responsive layout update to prevent squeezed cards */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-6 overflow-visible">
        
        {/* Left Column: Quest Content */}
        <div className="min-w-0 overflow-visible space-y-6">
          {/* Quest List Rendering */}
          {(() => {
            const categoryViewState = getQuestCategoryViewState({
              category: activeQuestTab,
              isProUser,
              quests
            });

            const visibleQuests = quests.filter((q) => {
              if (activeQuestTab === "all") return true;
              return q.cadence === activeQuestTab;
            });

            if (questsLoading) {
              return (
                <div className="py-12 flex flex-col items-center justify-center gap-2">
                  <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-[11px] font-mono font-bold text-slate-400">LOADING DAILY QUESTS...</p>
                </div>
              );
            }

            if (categoryViewState === "locked") {
              return (
                <div className="py-10 px-6 text-center border border-indigo-200 dark:border-indigo-900/60 rounded-2xl bg-indigo-50/50 dark:bg-indigo-950/40 shadow-3xs space-y-3">
                  <div className="w-12 h-12 rounded-2xl bg-indigo-100 border border-indigo-200 text-indigo-700 flex items-center justify-center mx-auto shadow-2xs">
                    <Lock className="w-6 h-6" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider">
                      Unlock Pro to Access These Quests
                    </h3>
                    <p className="text-xs text-slate-600 font-medium max-w-md mx-auto leading-relaxed">
                      Unlock Pro to access Pro quests and exclusive rewards.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onUpgradeClick}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-black text-xs shadow-sm hover:from-indigo-700 hover:to-violet-700 transition-all cursor-pointer"
                  >
                    <Sparkles className="w-4 h-4 text-amber-300 animate-pulse" />
                    <span>Unlock Pro Quests & Rewards</span>
                  </button>
                </div>
              );
            }

            if (categoryViewState === "completed") {
              return (
                <div className="py-12 text-center border border-dashed border-emerald-200 rounded-xl bg-emerald-50/20 space-y-1">
                  <p className="text-xs text-emerald-800 font-extrabold uppercase tracking-wider">All quests for this category are complete! 🎉</p>
                  <p className="text-[11px] text-emerald-600 font-medium">Great job! Check back for new daily or weekly challenges.</p>
                </div>
              );
            }

            if (visibleQuests.length === 0) {
              return (
                <div className="py-12 text-center border border-dashed border-slate-200 rounded-xl bg-slate-50/50 space-y-1">
                  <p className="text-xs text-slate-600 font-extrabold uppercase tracking-wider">No quests available in this category.</p>
                </div>
              );
            }

            return (
              <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4">
                {visibleQuests.map((quest) => {
                  const isCompleted = quest.is_completed;
                  const isClaimed = quest.is_claimed;
                  const progressPercent = Math.min(100, Math.round(((quest.current_progress || 0) / quest.target_count) * 100));
                  
                  const isProQuest = quest.cadence === "pro";
                  let QuestIcon = Target;
                  if (quest.event_type === "business_search") QuestIcon = Search;
                  else if (quest.event_type === "business_save") QuestIcon = Save;
                  else if (quest.event_type === "contact_lookup") QuestIcon = Zap;
                  else if (quest.event_type === "seo_audit") QuestIcon = Eye;
                  else if (quest.event_type === "sponsor_view") QuestIcon = ThumbsUp;
                  else if (quest.event_type === "profile_update") QuestIcon = CheckSquare;
                  else if (quest.event_type === "login") QuestIcon = Flame;
                  else if (quest.event_type === "lead_export") QuestIcon = Sparkles;

                  const questHowTo = getQuestActionInstructions(quest);

                  return (
                    <div 
                      key={quest.quest_key} 
                      className={`p-4 rounded-xl border transition-all flex flex-col justify-between space-y-3 relative overflow-hidden ${
                        isClaimed 
                          ? "bg-slate-50/70 border-slate-200 opacity-60" 
                          : isCompleted
                            ? "bg-emerald-50/35 border-emerald-300 shadow-3xs"
                            : isProQuest
                              ? isDark
                                ? "bg-gradient-to-br from-violet-950/40 via-slate-900 to-indigo-950/40 border-violet-850 hover:border-violet-700 shadow-sm"
                                : "bg-gradient-to-br from-violet-50/60 via-white to-indigo-50/30 border-violet-200 hover:border-violet-300 shadow-4xs hover:shadow-3xs"
                              : "bg-white border-slate-200 hover:shadow-3xs"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2.5 rounded-xl border shrink-0 ${
                          isClaimed
                            ? "bg-slate-250 text-slate-500 border-slate-350"
                            : isCompleted
                              ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                              : isProQuest
                                ? "bg-violet-100 text-violet-800 border-violet-200"
                                : "bg-indigo-50 text-indigo-700 border-indigo-100"
                        }`}>
                          <QuestIcon className="w-5 h-5" />
                        </div>
                        
                        <div className="space-y-0.5 text-left flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className={`text-[8.5px] font-mono font-black uppercase tracking-wide px-1.5 py-0.2 rounded border ${
                              quest.cadence === "daily" 
                                ? "bg-blue-50 text-blue-700 border-blue-150" 
                                : quest.cadence === "weekly"
                                  ? "bg-amber-50 text-amber-700 border-amber-150"
                                  : quest.cadence === "pro"
                                    ? "bg-gradient-to-r from-violet-600 to-indigo-600 text-white border-transparent"
                                    : "bg-purple-50 text-purple-700 border-purple-150"
                            }`}>
                              {quest.cadence === "pro" ? "Quest Pro" : quest.cadence}
                            </span>
                            {quest.pro_booster_duration && (
                              <span className="text-[8.5px] font-mono font-black uppercase tracking-wide px-1.5 py-0.2 rounded bg-gradient-to-r from-amber-500 to-indigo-600 text-white flex items-center gap-0.5 shadow-4xs">
                                <Zap className="w-2.5 h-2.5 fill-white text-white" /> {quest.cadence === "pro" ? "Pro Reward" : "XP Boost"}
                              </span>
                            )}
                          </div>
                          <h4 className="text-xs font-black text-slate-800 pt-1 truncate">{quest.name}</h4>
                          <p className="text-[10.5px] text-slate-500 leading-normal font-semibold" title={quest.description}>
                            {quest.description}
                          </p>
                        </div>
                      </div>

                      {/* Explicit How to Complete Box */}
                      <div className="p-2.5 bg-indigo-50/65 border border-indigo-100 rounded-lg text-left space-y-1">
                        <div className="flex items-center gap-1.5 text-[9.5px] font-black text-indigo-900 uppercase tracking-wider">
                          <CheckSquare className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                          <span>How to Complete:</span>
                        </div>
                        <p className="text-[10.5px] font-bold text-slate-750 leading-snug">
                          {questHowTo.shortInstruction}
                        </p>
                      </div>

                      {/* Progress slider / bar */}
                      <div className="space-y-1.5 pt-0.5">
                        <div className="flex justify-between items-center text-[10px] font-bold">
                          <span className="text-slate-450">Search Progress:</span>
                          <span className="font-mono text-slate-700 font-black">
                            {quest.current_progress || 0} / {quest.target_count} ({progressPercent}%)
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden border border-slate-200">
                          <div 
                            className={`h-full rounded-full transition-all duration-500 ${
                              isClaimed 
                                ? "bg-slate-400" 
                                : isCompleted 
                                  ? "bg-emerald-500" 
                                  : isProQuest
                                    ? "bg-violet-500"
                                    : "bg-indigo-600"
                            }`}
                            style={{ width: `${progressPercent}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Expandable Quest Objective & Rewards Details */}
                      <div className="pt-1 border-t border-slate-100/50">
                        <button
                          type="button"
                          onClick={() => setExpandedQuestId(expandedQuestId === quest.id ? null : quest.id)}
                          className="flex items-center gap-1 text-[9px] font-black text-indigo-600 hover:text-indigo-800 transition cursor-pointer"
                        >
                          <HelpCircle className="w-3 h-3 text-indigo-500" />
                          <span>{expandedQuestId === quest.id ? "HIDE OBJECTIVE DETAILS" : "STEP-BY-STEP GUIDE & REWARDS"}</span>
                          {expandedQuestId === quest.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </button>
                        
                        {expandedQuestId === quest.id && (
                          <div className="mt-2 p-3 bg-slate-50/90 rounded-xl border border-indigo-150 text-[10.5px] text-slate-700 space-y-2.5 animate-fadeIn text-left">
                            <div>
                              <span className="text-indigo-950 font-black uppercase text-[9.5px] tracking-wide block mb-1 flex items-center gap-1">
                                <Target className="w-3.5 h-3.5 text-indigo-600" /> Step-by-Step Instructions:
                              </span>
                              <ol className="list-decimal list-inside space-y-1 font-bold text-slate-800 pl-0.5">
                                {questHowTo.steps.map((step, idx) => (
                                  <li key={idx} className="leading-snug">{step}</li>
                                ))}
                              </ol>
                            </div>

                            <div className="pt-2 border-t border-slate-200/80">
                              <span className="text-indigo-950 font-black uppercase text-[9.5px] tracking-wide block mb-0.5">
                                💡 Why This Matters (Strategic Value):
                              </span>
                              <p className="text-[10px] text-slate-600 font-semibold leading-relaxed">
                                {quest.explanation || "Completing this quest builds your search habit and earns valuable XP rewards."}
                              </p>
                            </div>

                            <div className="pt-2 border-t border-slate-200/80 flex items-center justify-between text-[10px]">
                              <span className="font-black text-indigo-950">🎁 Reward Upon Completion:</span>
                              <span className="font-mono font-black text-indigo-700 bg-indigo-100/80 border border-indigo-200 px-2 py-0.5 rounded">
                                +{quest.points_reward} QP {quest.pro_booster_duration ? `+ ⚡ ${quest.pro_booster_duration}` : ''}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Rewards & claim action button */}
                      <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-slate-100/80">
                        <div className="flex flex-wrap gap-1.5">
                          <span className="text-[9px] font-mono text-indigo-700 font-black bg-indigo-50 border border-indigo-150 px-1.5 py-0.5 rounded">
                            +{quest.points_reward} QP
                          </span>
                          {quest.pro_booster_duration && (
                            <span className="text-[9px] font-mono text-amber-750 font-black bg-amber-50 border border-amber-150 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                              ⚡ {quest.pro_booster_duration} {quest.cadence === "pro" ? "Pro Boost" : "XP Boost"}
                            </span>
                          )}
                        </div>

                        {isClaimed ? (
                          <div className="text-[10px] font-bold text-slate-400 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4 text-slate-400" />
                            <span>Claimed ✓</span>
                          </div>
                        ) : isCompleted ? (
                          <div className="flex items-center gap-1.5">
                            <span className="text-[9px] font-mono font-black text-emerald-600 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded uppercase tracking-wider animate-pulse">
                              Complete
                            </span>
                            {isProQuest && !isProUser ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onUpgradeClick();
                                }}
                                className="px-2.5 py-1.2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white font-black text-[10px] rounded-lg shadow-4xs hover:shadow-3xs transition transform active:scale-95 cursor-pointer flex items-center gap-1"
                              >
                                <Lock className="w-3.5 h-3.5" />
                                <span>Unlock Pro to Claim</span>
                              </button>
                            ) : (
                              <button
                                type="button"
                                disabled={claimingQuestKey === quest.quest_key}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  claimQuest(quest.quest_key);
                                }}
                                className={`px-2.5 py-1.2 bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 text-white font-black text-[10px] rounded-lg shadow-4xs hover:shadow-3xs transition transform active:scale-95 flex items-center gap-1 ${
                                  claimingQuestKey === quest.quest_key ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
                                }`}
                              >
                                <span>{claimingQuestKey === quest.quest_key ? "Claiming..." : "Claim"}</span>
                                <Sparkles className="w-3 h-3 animate-pulse" />
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                            {isProQuest && <Lock className="w-3 h-3 text-violet-400/85" />}
                            <span>In Progress</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Unlocked Reputation Badges Section */}
          <div className="pt-4 border-t border-slate-200/65">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 mb-3">
              <h3 className="text-xs font-black uppercase tracking-wider text-slate-450 flex items-center gap-1.5">
                <Trophy className="w-3.5 h-3.5 text-indigo-500 animate-bounce" />
                <span>Earned Reputation Badges ({stats?.achievements?.filter((a: any) => a?.unlockedAt)?.length || 0} / {stats?.achievements?.length || 17} unlocked)</span>
              </h3>
              <button
                type="button"
                onClick={() => setIsBadgesModalOpen(true)}
                className="w-full sm:w-auto text-[11px] font-black text-indigo-600 hover:text-indigo-500 transition-colors flex items-center justify-center gap-1 bg-indigo-50 hover:bg-indigo-100/80 px-2.5 py-1.5 sm:py-1 rounded-lg border border-indigo-100 cursor-pointer shadow-4xs"
              >
                <span>View All Badges</span>
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>

            <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 pt-1 snap-x scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent">
              {(Array.isArray(stats?.achievements) ? stats.achievements : []).map((ach: any) => {
                const isUnlocked = !!ach.unlockedAt;
                return (
                  <div 
                    key={ach.id} 
                    className={`flex-none w-36 sm:w-44 snap-start p-3 rounded-xl border flex flex-col items-center text-center transition-all duration-350 relative group overflow-hidden ${
                      isUnlocked 
                        ? isDark 
                          ? "bg-indigo-950/40 border-indigo-500/50 text-slate-100 shadow-xs" 
                          : "bg-indigo-50/45 border-indigo-200/80 text-slate-800 shadow-xs" 
                        : isDark
                          ? "bg-slate-900/40 border-slate-800 text-slate-500 opacity-40 grayscale"
                          : "bg-slate-50/40 border-slate-150 text-slate-450 opacity-65 grayscale"
                    }`}
                  >
                    <span className="text-3xl mb-1.5 transform group-hover:scale-110 transition duration-300">{ach.icon || "🏅"}</span>
                    <p className="text-xs font-black tracking-tight line-clamp-1">{ach.title}</p>
                    <p className="text-[9.5px] text-slate-450 leading-tight mt-1 opacity-80 max-w-full text-slate-400 truncate group-hover:whitespace-normal group-hover:overflow-visible" title={ach.description}>
                      {ach.description}
                    </p>
                    <p className="text-[8px] font-mono font-bold tracking-wider uppercase mt-1.8 text-indigo-600">
                      {isUnlocked ? "🎉 Unlocked" : "Locked"}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Column: Active Quest Boosters / Monthly Milestones Card */}
        <div className="min-w-0 overflow-visible space-y-5">
          {/* Active Booster Card */}
          {(() => {
            const boosterDisplay = getActiveBoosterDisplay(isProUser, boosterTimeRemaining);
            return (
              <div className="p-4 bg-gradient-to-br from-indigo-900 via-slate-900 to-slate-950 text-white rounded-2xl space-y-3 border border-indigo-800 shadow-sm text-left">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-1.5">
                    <Zap className="w-4 h-4 text-amber-400 fill-amber-400" />
                    <span className="text-xs uppercase font-black tracking-wider text-white">
                      {boosterDisplay.displayName}
                    </span>
                  </div>
                  <span className={`text-[9.5px] font-black px-2 py-0.5 rounded-full uppercase tracking-wider ${
                    boosterDisplay.isActive ? "bg-amber-400 text-slate-950 animate-pulse" : "bg-slate-800 text-slate-400"
                  }`}>
                    {boosterDisplay.statusText}
                  </span>
                </div>

                <p className="text-[11px] text-slate-300 leading-relaxed font-medium">
                  {boosterDisplay.description}
                </p>

                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-indigo-800/80 text-[10px]">
                  <div>
                    <span className="text-indigo-300 block font-bold uppercase text-[9px]">Effect Amount</span>
                    <span className="font-extrabold text-amber-300">{boosterDisplay.effectText}</span>
                  </div>
                  <div>
                    <span className="text-indigo-300 block font-bold uppercase text-[9px]">Duration / Status</span>
                    <span className="font-extrabold text-white font-mono">{boosterDisplay.timeRemaining}</span>
                  </div>
                </div>

                <div className="text-[9.5px] text-indigo-300 font-semibold pt-1 border-t border-indigo-800/50 flex justify-between items-center">
                  <span>Access: {boosterDisplay.accessText}</span>
                  {!isProUser && (
                    <button
                      type="button"
                      onClick={onUpgradeClick}
                      className="text-amber-300 hover:underline font-bold cursor-pointer"
                    >
                      Upgrade for Pro Boost
                    </button>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Monthly Milestones Card */}
          <div className="p-4 bg-gradient-to-r from-slate-900 to-indigo-950 text-white rounded-xl space-y-3.5 border border-indigo-900">
            <div className="flex justify-between items-center">
              <span className="text-[10px] uppercase font-black tracking-wider text-indigo-300">Monthly Milestones</span>
              <span className="text-[10px] bg-indigo-500 text-white font-black px-1.5 py-0.1 rounded">
                {new Date().toLocaleString('en-US', { month: 'long' })}
              </span>
            </div>
            
            <div className="space-y-2.5 text-left">
              {/* Milestone 1 */}
              <div className="space-y-1">
                <div className="flex justify-between text-[10px] font-bold">
                  <span className="text-slate-300">Local Legend Streak</span>
                  <span className="text-indigo-200 font-mono">
                    {Math.min(15, stats?.monthlyMilestones?.activeDaysCount ?? stats?.streak ?? 0)} / 15 Days
                  </span>
                </div>
                <div className="w-full bg-slate-800 h-1 rounded-full overflow-hidden">
                  <div 
                    className="bg-indigo-400 h-full rounded-full transition-all duration-300" 
                    style={{ width: `${Math.min(100, (((stats?.monthlyMilestones?.activeDaysCount ?? stats?.streak ?? 0) / 15) * 100))}%` }}
                  ></div>
                </div>
              </div>

              {/* Milestone 2 */}
              <div className="space-y-1">
                <div className="flex justify-between text-[10px] font-bold">
                  <span className="text-slate-300">Badge Collector Tycoon</span>
                  <span className="text-indigo-200 font-mono">
                    {stats?.monthlyMilestones?.badgesUnlockedCount ?? (stats?.achievements?.filter((a: any) => a?.unlockedAt)?.length || 0)} / 4 Badges
                  </span>
                </div>
                <div className="w-full bg-slate-800 h-1 rounded-full overflow-hidden">
                  <div 
                    className="bg-emerald-400 h-full rounded-full transition-all duration-300" 
                    style={{ width: `${Math.min(100, (((stats?.monthlyMilestones?.badgesUnlockedCount ?? (stats?.achievements?.filter((a: any) => a?.unlockedAt)?.length || 0)) / 4) * 100))}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};
